import React, { useMemo } from 'react';
import BarcodeSVG, { isBarcodeSupported } from './BarcodeSVG';
import QRCodeSVG from './QRCodeSVG';
import './ProductLabel.css';

/**
 * Safely parse particulars from JSON, Python string dicts or objects
 */
export function safeParseParticulars(val) {
  if (!val) return {};
  if (typeof val === 'object') return val;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed || trimmed === '{}') return {};
    try {
      return JSON.parse(trimmed);
    } catch (e) {
      try {
        const fixed = trimmed.replace(/'/g, '"');
        return JSON.parse(fixed);
      } catch (e2) {
        return {};
      }
    }
  }
  return {};
}

/**
 * Format particularities for the top highlighted box
 * Supports multiple selected particularities: e.g. "TAM: 37 • COR: PRETO"
 * When selectedKeys is an empty array [], all particularities are disabled,
 * returning '' so the label displays the standard fallback (REF: #ID).
 */
export function formatParticularitiesText(particulars, selectedKeys = null, customText = '') {
  if (customText && customText.trim()) {
    return customText.trim().toUpperCase();
  }

  // Explicit empty array means the user disabled all particularities
  if (Array.isArray(selectedKeys) && selectedKeys.length === 0) {
    return '';
  }

  const parsed = safeParseParticulars(particulars);
  if (!parsed || typeof parsed !== 'object') return '';

  const entries = Object.entries(parsed);
  if (entries.length === 0) return '';

  // If specific keys are provided, filter strictly by them
  if (Array.isArray(selectedKeys)) {
    const activeEntries = entries.filter(([k]) =>
      selectedKeys.some((sk) => sk.toLowerCase() === k.toLowerCase()),
    );
    if (activeEntries.length === 0) {
      return '';
    }
    return activeEntries
      .map(([k, v]) => formatSingleParticular(k, v))
      .filter(Boolean)
      .join(' • ');
  }

  // Fallback if selectedKeys is null or undefined: show all available
  return entries
    .map(([k, v]) => formatSingleParticular(k, v))
    .filter(Boolean)
    .join(' • ');
}

function formatSingleParticular(key, value) {
  if (!value) return '';
  const valStr = String(value).trim().toUpperCase();
  const kLower = String(key).trim().toLowerCase();

  if (kLower === 'tamanho' || kLower === 'tam') {
    return `TAM: ${valStr}`;
  }
  if (kLower === 'cor') {
    return `COR: ${valStr}`;
  }
  if (kLower === 'voltagem' || kLower === 'volt') {
    return `VOLT: ${valStr}`;
  }
  return `${key.toUpperCase()}: ${valStr}`;
}

const ProductLabel = ({
  product = {},
  selectedParticulars = null,
  customTopText = '',
  codeType = 'auto', // 'auto' | 'barcode' | 'qrcode'
  showCutLine = true,
  showLateralId = true,
  showPrice = true,
  scale = 1,
  className = '',
  style = {},
}) => {
  const {
    id = '',
    name = '',
    barCode = '',
    value = 0,
    particulars = null,
  } = product;

  // Format top box text with multiple selected particularities
  const topBoxText = useMemo(() => {
    const text = formatParticularitiesText(particulars, selectedParticulars, customTopText);
    return text || `REF: #${id || '0000'}`;
  }, [particulars, selectedParticulars, customTopText, id]);

  // Format price into integer and superscript cents
  const { integerPart, centsPart } = useMemo(() => {
    const num = Number(value) || 0;
    const fixed = num.toFixed(2);
    const [integ, cents] = fixed.split('.');
    return {
      integerPart: integ,
      centsPart: cents,
    };
  }, [value]);

  const barcodeValue = String(barCode || id || '000000').trim();

  // Automatic fallback to QRCode when code cannot be supported by 1D Barcode
  const effectiveCodeType = useMemo(() => {
    if (codeType === 'qrcode') return 'qrcode';
    const supported = isBarcodeSupported(barcodeValue);
    if (!supported) return 'qrcode';
    return 'barcode';
  }, [codeType, barcodeValue]);

  return (
    <div
      className={`product-label-card ${scale !== 1 ? 'product-label-preview-shadow' : ''} ${className}`}
      style={{
        transform: scale !== 1 ? `scale(${scale})` : undefined,
        transformOrigin: 'top center',
        ...style,
      }}
    >
      {/* 1. TOPO: Caixa de Particularidade + ID Lateral */}
      <div className="product-label-top-row">
        <div className="product-label-particulars-box">
          <span className="product-label-particulars-text">{topBoxText}</span>
        </div>
        {showLateralId && (
          <div className="product-label-lateral-id">
            #{id}
          </div>
        )}
      </div>

      {/* 2. CÓDIGO DE BARRAS OU QR CODE (COM FALLBACK AUTOMÁTICO) */}
      <div className="product-label-barcode-section">
        {effectiveCodeType === 'qrcode' ? (
          <QRCodeSVG value={barcodeValue} size={50} showText />
        ) : (
          <BarcodeSVG value={barcodeValue} height={32} showText />
        )}
      </div>

      {/* 3. LINHA DE PICOTE COM TESOURAS */}
      {showCutLine && (
        <div className="product-label-cut-line-container">
          <span className="product-label-scissors">✂</span>
          <div className="product-label-cut-dashes" />
          <span className="product-label-scissors" style={{ transform: 'scaleX(-1)' }}>✂</span>
        </div>
      )}

      {/* 4. PREÇO EM DESTAQUE */}
      {showPrice && (
        <div className="product-label-price-section">
          <span className="product-label-currency">R$</span>
          <span className="product-label-price-integer">{integerPart}</span>
          <span className="product-label-price-cents">.{centsPart}</span>
          <span className="product-label-price-zero-exponent">⁰</span>
        </div>
      )}

      {/* 5. NOME DO PRODUTO (campo 'name' do cadastro) */}
      <div className="product-label-product-name" title={name}>
        {name || 'PRODUTO SEM NOME'}
      </div>
    </div>
  );
};

export default ProductLabel;
